import React, { useContext } from 'react';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
// import { animated, useSpring } from 'react-spring';
// import { AnchorLink } from 'gatsby-plugin-anchor-links';
import localize from '../components/localize';
import SEO from '../components/SEO';
import LocaleBlock from '../components/LocaleBlock';
import PropertyTypesPreview from '../components/PropertyTypesPreview';
import Legend from '../components/Legend';
import Collapsible from '../components/Collapsible';
import MasterPlan from '../components/MasterPlan';
import Vimeo from '../components/Vimeo';
import Timeline from '../components/Timeline';
import Footer from '../components/Footer';
import { tr } from '../utils/translations';
import LocaleContext from '../components/LocaleContext';
import LocaleLink from '../components/LocaleLink';
import { LandingPageStyles } from '../styles/LandingPageStyles';
import Gallery from '../components/Gallery';

export const query = graphql`
  query landingPageQuery($imageMaxWidth: Int!) {
    siteSettings: allSanitySiteSettings {
      nodes {
        ...SiteSettingsFields
      }
    }
    timelineItems: allSanityTimelineItem(sort: { fields: date }) {
      nodes {
        id
        name {
          ...LocaleStringFields
        }
        linkSlug {
          ...LocaleStringFields
        }
        className
        date
        _rawContent
      }
    }
    propertyTypes: allSanityPropertyType(
      filter: { hidden: { ne: true } }
      sort: { fields: order }
    ) {
      nodes {
        name {
          ...LocaleStringFields
        }
        subtitle {
          ...LocaleStringFields
        }
        slug {
          ...LocaleStringFields
        }
        introduction {
          ...LocaleBlockFields
        }
        image {
          asset {
            fluid(maxWidth: 800) {
              ...GatsbySanityImageFluid
            }
          }
        }
        className
        type
        heroVideoVimeoID
        bookURL
      }
    }
    landingPages: allSanityLandingPage {
      nodes {
        name {
          ...LocaleStringFields
        }
        metaName {
          ...LocaleStringFields
        }
        metaDescription {
          ...LocaleStringFields
        }
        metaImage {
          asset {
            fluid(maxWidth: $imageMaxWidth) {
              ...GatsbySanityImageFluid
            }
          }
        }
        introText {
          ...LocaleBlockFields
        }
        visionText {
          ...LocaleBlockFields
        }
        visionQuote {
          ...LocaleBlockFields
        }
        visionImage {
          _type
          asset {
            fluid(maxWidth: $imageMaxWidth) {
              ...GatsbySanityImageFluid
            }
          }
        }
        masterPlanTitle {
          ...LocaleStringFields
        }
        masterPlanImage {
          _type
          asset {
            fluid(maxWidth: $imageMaxWidth) {
              ...GatsbySanityImageFluid
            }
          }
        }
        masterPlanLocation {
          ...LocaleBlockFields
        }
        masterPlanAirport {
          ...LocaleBlockFields
        }
        masterPlanPhase {
          ...LocaleBlockFields
        }
        propertyTitle {
          ...LocaleStringFields
        }
        propertyText {
          ...LocaleBlockFields
        }
        stayNowImage {
          asset {
            fluid(maxWidth: $imageMaxWidth) {
              ...GatsbySanityImageFluid
            }
          }
        }
        stayNowGallery {
          _rawImages
        }
        stayNowTitle {
          ...LocaleStringFields
        }
        stayNowText {
          ...LocaleBlockFields
        }
        locationTitle {
          ...LocaleStringFields
        }
        locationText {
          ...LocaleBlockFields
        }
        locationImage {
          asset {
            fluid(maxWidth: $imageMaxWidth) {
              ...GatsbySanityImageFluid
            }
          }
        }
        _rawLocationLegends
        locationFlightsTitle {
          ...LocaleStringFields
        }
        locationFlights {
          ...LocaleBlockFields
        }
        locationTownsTitle {
          ...LocaleStringFields
        }
        locationTowns {
          ...LocaleBlockFields
        }
        comingSoonHeroTitle {
          ...LocaleStringFields
        }
        comingSoonHeroVimeoID
        comingSoonTitle {
          ...LocaleStringFields
        }
        comingSoonText {
          ...LocaleBlockFields
        }
      }
    }
  }
`;

const LandingPage = ({ data, pageContext }) => {
  const [locale, setLocale] = useContext(LocaleContext);
  const page = data.landingPages.nodes[0];
  const siteSettings = data.siteSettings.nodes[0];
  const { propertyTypes, timelineItems } = data;
  const propertyTypesBuy = propertyTypes.nodes.filter(
    (propertyType) => propertyType.type === 'buy'
  );
  const propertyTypesRent = propertyTypes.nodes.filter(
    (propertyType) => propertyType.type === 'rent'
  );

  // Parallax stuff (keeping for reference only)
  // const ref = useRef();
  // const [{ offset }, set] = useSpring(() => ({ offset: 0 }));
  // const calc = (o) => `translateY(${o > 0 ? o * -0.075 : 0}px)`;
  // const handleScroll = () => {
  //   const posY = ref.current.getBoundingClientRect().top;
  //   const offset = window.pageYOffset - posY;
  //   set({ offset });
  // };
  // useEffect(() => {
  //   window.addEventListener('scroll', handleScroll);
  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   };
  // });
  // End parallax stuff

  return (
    <>
      <SEO
        title={page.metaName}
        description={page.metaDescription}
        image={page.metaImage.asset.fluid.src}
      />
      {page && (
        <LandingPageStyles>
          <section className="intro">
            <Vimeo vimeoID="793506718" heightRatio={56} className="hero" />

            <div className="content">
              <LocaleBlock content={page.introText} />
              <a
                className="button cta"
                href="http://www.stayatmusa.mx/hotelito"
              >
                {tr('cta', 'exploreHotelito', locale)}
              </a>
              {/* <LocaleLink className="button cta" to="/casa-musa">
                {tr('cta', 'bookNow', locale)}
              </LocaleLink> */}
            </div>
          </section>

          <section className="masterPlan">
            <div className="sectionBigTitle">
              <LocaleBlock content={page.masterPlanPhase} className="phase" />
              <h2>{page.masterPlanTitle}</h2>
            </div>

            <MasterPlan
              title={page.masterPlanTitle}
              imageWithAlt={page.masterPlanImage}
              locationText={page.masterPlanLocation}
              airportText={page.masterPlanAirport}
              phaseText={page.masterPlanPhase}
            />
            <div className="quote" data-sal="fade" data-sal-duration="1500">
              <LocaleBlock content={page.visionQuote} />
            </div>
          </section>

          <section className="property">
            <header data-sal="fade" data-sal-duration="1500">
              <a id="property"> </a>
              <h2>{page.propertyTitle}</h2>
              {/* <LocaleBlock className="intro" content={page.propertyText} /> */}
            </header>
            <PropertyTypesPreview propertyTypes={propertyTypesBuy} carousel />
          </section>

          <section className="stayNow">
            <div className="hero">
              <Img
                fluid={page.stayNowImage.asset.fluid}
                alt={tr('titles', 'freedomIsAJungle', locale)}
              />
            </div>
            <h2>{page.stayNowTitle}</h2>
            <div className="content">
              <Gallery
                images={page.stayNowGallery._rawImages}
                imageMaxWidth={pageContext.imageMaxWidth}
                autoplay
                arrows={false}
              />

              <div>
                <LocaleBlock className="intro" content={page.stayNowText} />
                {/* Link to Stay Now page */}
                <div className="buttonWrapper">
                  <LocaleLink
                    className="button cta"
                    to="/stay-now"
                    slugs={{ es: 'visita-nos' }}
                  >
                    {tr('cta', 'viewStays', locale)}
                  </LocaleLink>
                </div>
              </div>
            </div>
          </section>

          {/* TODO: break out this section? */}
          <section className="location">
            <Vimeo className="hero" vimeoID={493788075} heightRatio={67} />
            <div className="contentWrapper">
              {/* <a id="location" className="anchor">
                {' '}
              </a> */}
              <div className="map">
                <p className="legend coordinates">17°25'21"N 101°11'48"W</p>

                <h2 data-sal="fade" data-sal-duration="1500">
                  {page.locationTitle}
                </h2>

                {/* <Img fluid={page.locationImage.asset.fluid} /> */}
                <div data-sal="fade" data-sal-duration="1500">
                  {page._rawLocationLegends &&
                    page._rawLocationLegends.map((legend) => (
                      <Legend
                        key={legend._key}
                        name={legend.name}
                        text={legend.text}
                        className={legend.className}
                      />
                    ))}
                </div>
              </div>
              <div className="content" data-sal="fade" data-sal-duration="1500">
                <LocaleBlock content={page.locationText} />
                <div className="collapsibles">
                  {page.locationFlights && (
                    <Collapsible
                      title={page.locationFlightsTitle}
                      text={page.locationFlights}
                      defaultCollapsed
                    />
                  )}
                  {page.locationTowns && (
                    <Collapsible
                      className="towns"
                      title={page.locationTownsTitle}
                      text={page.locationTowns}
                      defaultCollapsed
                    />
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="comingSoon">
            <Vimeo
              vimeoID={page.comingSoonHeroVimeoID}
              heightRatio={50}
              allowExpand
              className="hero"
            />
            <div className="content" data-sal="fade" data-sal-duration="1500">
              <a id="coming-soon" className="anchor">
                {' '}
              </a>
              <p className="upperCase preTitle mb">
                {tr('titles', 'timeline', locale)}
              </p>
              <h2>{page.comingSoonTitle}</h2>
              <LocaleBlock content={page.comingSoonText} />
            </div>
          </section>

          <section className="timeline">
            <a id="timeline" className="anchor">
              <Timeline
                name={tr('titles', 'timeline', locale)}
                timelineItems={timelineItems}
                imageMaxWidth={pageContext.imageMaxWidth}
              />
            </a>
          </section>
        </LandingPageStyles>
      )}
      {siteSettings && <Footer siteSettings={siteSettings} />}
    </>
  );
};

export default localize(LandingPage);
